import { useImmer } from "use-immer";
import { useEffect, useCallback } from "react";
import { DEFAULT_PAGINATION } from "../../../utils/constants";
import { getSegmentReferencesApi } from "../api";

export const useSegmentReferences = (segmentId) => {
  const [state, setState] = useImmer({
    isLoading: false,
    references: [],
    pagination: DEFAULT_PAGINATION,
    searchText: "",
    referenceType: null,
  });

  const fetchReferences = useCallback(
    async (pageIndex, pageSize, search = "", referenceType = null) => {
      setState((draft) => {
        draft.isLoading = true;
      });

      try {
        const query = {
          params: {
            ...(search ? { search } : {}),
            ...(referenceType?.value ? { used_in: referenceType.value } : {}),
          },
        };
        const nextPageIndex = pageIndex ?? state.pagination.pageIndex;
        const nextPageSize = pageSize ?? state.pagination.pageSize;
        const response = await getSegmentReferencesApi(
          query,
          { pageIndex: nextPageIndex, pageSize: nextPageSize },
          segmentId,
        );

        if (response.success) {
          setState((draft) => {
            draft.references = response.data ?? [];
            draft.pagination = response.pagination ?? DEFAULT_PAGINATION;
            draft.isLoading = false;
          });
        } else {
          setState((draft) => {
            draft.isLoading = false;
          });
        }
      } catch (error) {
        setState((draft) => {
          draft.isLoading = false;
        });
      }
    },
    [
      segmentId,
      setState,
      state.pagination.pageIndex,
      state.pagination.pageSize,
    ],
  );

  const onGotoPage = (pageIndex) => {
    setState((draft) => {
      draft.pagination.pageIndex = pageIndex;
    });
    fetchReferences(
      pageIndex,
      state.pagination.pageSize,
      state.searchText,
      state.referenceType,
    );
  };

  const onSetPageSize = (e) => {
    const nextPageSize = +e.target.value;
    setState((draft) => {
      draft.pagination.pageSize = nextPageSize;
    });
    fetchReferences(1, nextPageSize, state.searchText, state.referenceType);
  };

  const onSearch = (searchText) => {
    setState((draft) => {
      draft.searchText = searchText;
    });
    fetchReferences(
      1,
      state.pagination.pageSize,
      searchText,
      state.referenceType,
    );
  };

  const onReferenceTypeChange = (_, referenceType) => {
    setState((draft) => {
      draft.referenceType = referenceType;
    });
    fetchReferences(
      1,
      state.pagination.pageSize,
      state.searchText,
      referenceType,
    );
  };

  useEffect(() => {
    if (segmentId) {
      fetchReferences(
        state.pagination.pageIndex,
        state.pagination.pageSize,
        state.searchText,
        state.referenceType,
      );
    }
  }, []);

  return {
    state,
    onGotoPage,
    onSetPageSize,
    onSearch,
    onReferenceTypeChange,
  };
};
